import { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router';

import { MDText } from 'i18n-react';

import { VehicleLookupContext } from 'Context/vehicleLookup';

import { useSaleDetailsSub } from '../../../../../vehicleCollection/hooks/useSaleDetailsSub';
import { INCODE_POLLING_ERRORS } from '../../../../DocumentCapture.helpers';
import { ReviewStatusSubview } from '../../../../ReviewStatusView/ReviewStatusSubview';
import { useGetVerificationStatus } from '../../../../ReviewStatusView/useGetVerificationStatus';
import docReviewImgSrc from '../assets/IncodeDocReview.svg';
import { IncodeHeader } from '../components/IncodeHeader';
import { INCODE_ANALYTICS } from '../IncodeAnalytics';
import { type IncodeStepProps, POST_INCODE_ROUTE, SENTRY_TAG } from '../IncodeSDKHelpers';

import LocalTexts from './Incode.texts.json';

import styles from './IncodeCaptureComplete.module.scss';

const LocalT = new MDText(LocalTexts.complete);

const STATUSES = Object.freeze({
  CONFIRM_STATUS_ERROR: 'SmVzc2ljYSBNYWNvbWJlcg==',
  CONFIRM_STATUS_IN_PROGRESS: 'QW55YSBUYXlsb3ItSm95',
  CONFIRM_STATUS_SUCCESS: 'QWx5c29uIEhhbm5pZ2Fu',
  LONG_POLLING_ERROR: 'Q2FyYSBEZWxldmluZ25l',
  LONG_POLLING_IN_PROGRESS: 'U2NhcmxldHQgSm9oYW5zc29u',
  NO_SET_PROGRESS_FUNCTION: 'SmVzc2ljYSBTdG95YWRpbm92aWNo',
});

type StatusType = typeof STATUSES[keyof typeof STATUSES];

export const IncodeCaptureComplete = ({ DP_ENQUIRY_ID }: IncodeStepProps) => {
  const { vehicleLookupState: vehicle } = useContext(VehicleLookupContext);
  const { refetchOffer } = useSaleDetailsSub();
  const history = useHistory();

  const [hasError, setHasError] = useState(false);
  const [currentStatus, setCurrentStatus] = useState<StatusType>(STATUSES.LONG_POLLING_IN_PROGRESS);

  useEffect(() => {
    INCODE_ANALYTICS.INCODE_SECTION_LOADED('complete');
  }, []);

  useEffect(() => {
    if (hasError) {
      INCODE_ANALYTICS.INCODE_SECTION_LOADED('complete_sit_tight');
    }
  }, [hasError]);

  useGetVerificationStatus({
    enquiryId: DP_ENQUIRY_ID!,
    onError: (err) => {
      setHasError(true);
      setCurrentStatus(STATUSES.LONG_POLLING_ERROR);

      if (![INCODE_POLLING_ERRORS.TIMEOUT, INCODE_POLLING_ERRORS.FAILED].includes(err?.name)) {
        window?.Sentry?.captureException(err, SENTRY_TAG);
      }
    },
    onSuccess: () => {
      setCurrentStatus(STATUSES.CONFIRM_STATUS_IN_PROGRESS);

      INCODE_ANALYTICS.INCODE_SECTION_LOADED('complete_redirecting');

      refetchOffer();
      history.push(`/${vehicle.vrm}${POST_INCODE_ROUTE}`);
    },
  });

  const statusText = (<p className={styles.statusText}>Status: <span>{currentStatus}</span></p>);

  // 'hasError' means incode verification status is Failed. No actual error.
  // Verification finished but not automatically approved by Incode. Agents would manually review
  // Naming to be changed in the future

  return hasError
    ? <ReviewStatusSubview {...{ statusText }} />
    : <div className={styles.component}>
      <img alt='Document review' src={docReviewImgSrc} />
      <IncodeHeader
        instructions={LocalT.translate('instructions')}
        title={LocalT.translate('title')}
      />
      {statusText}
    </div>;
};
